import React, { useCallback, useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useQueries, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams, useSearchParams } from "react-router-dom"

import { useMediaQuery, useTheme } from "@mui/material"
import Grid from "@mui/material/Grid"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"

import { KonanActivityCard } from "../components/cards/KonanActivityCard"
import { KonanLastFeedbackCard } from "../components/cards/KonanLastFeedbackCard"
import { DataDriftBarChart } from "../components/graphs/Bar/DataDriftBarChart"
import { RequestsBarChart } from "../components/graphs/Bar/RequestsBarChart"
import { ResponseTimeBarChart } from "../components/graphs/Bar/ResponseTimeBarChart"
import { UsageBarChart } from "../components/graphs/Bar/UsageBarChart"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { ModelAverageCpuUsage, baseErrorType } from "../types/custom/projects"
import type { DeploymentLatestFeedbackDateTime } from "../types/generated/api/DeploymentLatestFeedbackDateTime"
import type { DeploymentModelsAvgResponseTime } from "../types/generated/api/DeploymentModelsAvgResponseTime"
import type { DeploymentPredictionsSummary } from "../types/generated/api/DeploymentPredictionsSummary"
import type { DeploymentUsage } from "../types/generated/api/DeploymentUsage"
import type { ListModelStateSwitch } from "../types/generated/api/ListModelStateSwitch"
import type { Model } from "../types/generated/api/Model"
import { ModelListCreate } from "../types/generated/api/ModelListCreate"
import type { PaginatedListModelStateSwitchList } from "../types/generated/api/PaginatedListModelStateSwitchList"
import type { PaginatedModelListCreateList } from "../types/generated/api/PaginatedModelListCreateList"
import { useIsPermitted } from "../utils/PermissionsHelpers"
import { getAverageCpuUsageForModel, getAverageRamUsageForModel } from "../utils/deploymentDetailsHelpers"
import { convertDataDriftListToChart, getModelByUUID } from "../utils/modelDetailsHelpers"
import { orderModelsByStatus } from "../utils/searchSortFilterHelpers"

type ParamsType = {
  id: string
}

/**
 * Model overview screen component
 * @return {React.ReactElement}
 */
export function Overview(): React.ReactElement {
  const { id: projectId } = useParams<ParamsType>()

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canViewHistory = useIsPermitted("View model state switch history", permissions.konan, flattenedKonanPermissions)

  const queryClient = useQueryClient()

  const theme = useTheme()
  const { t } = useTranslation("overview")

  const [searchParams] = useSearchParams()

  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  const graphHeight = 200
  const chartsBaseHeight = 50

  const {
    isLoading: isModelsLoading,
    data: models,
    isFetchedAfterMount: isModelFetchedAfterMount,
  } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError<baseErrorType>>(
    ["overviewModels", projectId],
    () => KonanAPI.fetchModels({ projectId: projectId as string, pageSize: 1000 }),
  )

  //function that returns adjusted models based on search, sort, and filter values
  const adjustedModels = useMemo((): Model[] => {
    return models?.data?.results ? orderModelsByStatus(models?.data?.results, "asc") : []
  }, [models])

  const dataDriftResponseList = useQueries(
    models?.data?.results?.map((model) => ({
      queryKey: ["overviewDriftJobs", model.uuid],
      queryFn: () => KonanAPI.fetchDataDriftJobs(model.uuid),
    })) ?? [],
  )

  const CPUAndRAMResponseList = useQueries(
    models?.data?.results
      ?.filter((model) => model.state !== "disabled")
      .map(
        (model) => ({
          queryKey: ["cpuAndRamUsage", model.uuid, startDate, endDate],
          queryFn: () => KonanAPI.fetchModelCpuAndRamUsage(model.uuid, startDate as string, endDate as string),
        }),
        {
          enabled: startDate != null && endDate != null,
        },
      ) ?? [],
  )

  const { isLoading: isApiRequestsLoading, data: apiRequests } = useQuery<AxiosResponse<DeploymentPredictionsSummary>>(
    ["apiRequests", projectId, startDate, endDate],
    () =>
      KonanAPI.fetchApiRequestsSummary({
        project_uuid: projectId as string,
        start_date: startDate as string,
        end_date: endDate as string,
      }),
    {
      enabled: !!projectId && !!startDate && !!endDate,
    },
  )

  const { isLoading: isAverageResponseTimeLoading, data: averageResponseTime } = useQuery<
    AxiosResponse<DeploymentModelsAvgResponseTime>,
    AxiosError<baseErrorType>
  >(
    ["averageResponseTime", projectId, startDate, endDate],
    () =>
      KonanAPI.fetchProjectAverageResponseTime({
        start_date: startDate as string,
        end_date: endDate as string,
        project_uuid: projectId as string,
      }),
    {
      enabled: projectId !== "" && startDate != null && endDate != null,
    },
  )

  const { isLoading: isLastFeedbackLoading, data: lastFeedbackResponse } = useQuery<
    AxiosResponse<DeploymentLatestFeedbackDateTime>
  >(["lastFeedback", projectId], () => KonanAPI.fetchLastFeedback(projectId as string), {
    enabled: projectId !== "",
  })

  const { isLoading: isActivityLoading, data: activityResponse } = useQuery<
    PaginatedListModelStateSwitchList,
    AxiosError<baseErrorType>
  >(["modelHistory", projectId], () => KonanAPI.getProjectHistory(projectId as string), {
    enabled: projectId !== "",
  })

  const dataDriftChartData = useMemo(() => {
    if (models?.data?.count && models.data.count > 0) {
      return convertDataDriftListToChart(dataDriftResponseList, models.data.results as Model[])
    }
    return []
  }, [dataDriftResponseList, models])

  const isDataDriftLoading = useMemo(() => {
    if (dataDriftResponseList && dataDriftResponseList.length > 0 && !(startDate && !endDate ? true : false)) {
      if (dataDriftResponseList.every((item) => item.isLoading === false)) {
        return false
      }
    }
    if (adjustedModels.length === 0 && isModelFetchedAfterMount) return false

    return true
  }, [adjustedModels.length, dataDriftResponseList, endDate, isModelFetchedAfterMount, startDate])

  const CPUChartData = useMemo(() => {
    const result: ModelAverageCpuUsage[] = []
    if (models?.data?.count && models.data.count > 0) {
      CPUAndRAMResponseList.forEach((item, index) => {
        result.push(
          getAverageCpuUsageForModel(
            models.data.results?.[index] as ModelListCreate,
            item?.data?.data as DeploymentUsage,
          ),
        )
      })
    }
    return result.every((item) => item.realMax === 0) ? [] : result
  }, [CPUAndRAMResponseList, models])

  const RAMChartData = useMemo(() => {
    const result: ModelAverageCpuUsage[] = []
    if (models?.data?.count && models.data.count > 0) {
      CPUAndRAMResponseList.forEach((item, index) => {
        result.push(
          getAverageRamUsageForModel(
            models.data.results?.[index] as ModelListCreate,
            item?.data?.data as DeploymentUsage,
          ),
        )
      })
    }
    return result.every((item) => item.realMax === 0) ? [] : result
  }, [CPUAndRAMResponseList, models])

  const isCPUAndRAMLoading = useMemo(() => {
    if (CPUAndRAMResponseList && CPUAndRAMResponseList.length > 0 && !(startDate && !endDate ? true : false)) {
      if (CPUAndRAMResponseList.every((item) => item.isLoading === false)) {
        return false
      }
    }
    if (adjustedModels.length === 0 && isModelFetchedAfterMount) return false
    return true
  }, [CPUAndRAMResponseList, adjustedModels.length, endDate, isModelFetchedAfterMount, startDate])

  const requestsGraphHeight = useMemo(() => {
    return apiRequests?.data.results.length ? chartsBaseHeight + apiRequests?.data.results.length * 40 : graphHeight
  }, [apiRequests])

  const averageResponseTimeGraphHeight = useMemo(() => {
    return averageResponseTime?.data.results.length
      ? chartsBaseHeight + averageResponseTime?.data.results.length * 40
      : graphHeight
  }, [averageResponseTime])

  const requestsAndResponseTimeHeight = useMemo(() => {
    return Math.max(requestsGraphHeight, averageResponseTimeGraphHeight, graphHeight)
  }, [averageResponseTimeGraphHeight, requestsGraphHeight])

  const cpuGraphHeight = useMemo(() => {
    return CPUChartData?.length && CPUChartData?.some((item) => item.realMax !== 0) && !isCPUAndRAMLoading
      ? chartsBaseHeight + CPUChartData.filter((item) => item.realMax !== 0).length * 40
      : graphHeight
  }, [CPUChartData, isCPUAndRAMLoading])

  const ramGraphHeight = useMemo(() => {
    return RAMChartData?.length && RAMChartData?.some((item) => item.realMax !== 0) && !isCPUAndRAMLoading
      ? chartsBaseHeight + RAMChartData.filter((item) => item.realMax !== 0).length * 40
      : graphHeight
  }, [RAMChartData, isCPUAndRAMLoading])

  const dataDriftGraphHeight = useMemo(() => {
    return dataDriftChartData?.length && !isDataDriftLoading
      ? chartsBaseHeight + dataDriftChartData.length * 40
      : graphHeight
  }, [dataDriftChartData, isDataDriftLoading])

  const driftAndUsageHeight = useMemo(() => {
    return Math.max(dataDriftGraphHeight, cpuGraphHeight, ramGraphHeight, graphHeight)
  }, [cpuGraphHeight, dataDriftGraphHeight, ramGraphHeight])

  const activityGraphHeight = useMemo(() => {
    return requestsAndResponseTimeHeight + driftAndUsageHeight + 68
  }, [driftAndUsageHeight, requestsAndResponseTimeHeight])

  // Add model name to activity response
  const memoizedGetModelByUUID = useCallback((uuid: string) => getModelByUUID(uuid, models?.data?.results), [models])
  const activityData = useMemo(() => {
    if (activityResponse?.results) {
      return activityResponse?.results?.map((item) => ({
        ...item,
        name: memoizedGetModelByUUID(item.model)?.name,
      }))
    }
    return []
  }, [activityResponse?.results, memoizedGetModelByUUID])

  //reset CPU, RAM and Data Drift states whenever the user deletes a model
  useEffect(() => {
    queryClient.invalidateQueries("apiRequests")
    queryClient.invalidateQueries("averageResponseTime")
    queryClient.invalidateQueries("overviewDriftJobs")
    queryClient.invalidateQueries("cpuAndRamUsage")
  }, [models, queryClient])

  return (
    <Grid container spacing={2}>
      {/* Overview section */}
      <Grid item xs={12} lg={canViewHistory ? 9 : 12}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <RequestsBarChart
              title={t("requests.title")}
              data={apiRequests?.data.results ?? []}
              models={models?.data?.results ? (models?.data?.results as Model[]) : []}
              graphHeight={requestsAndResponseTimeHeight}
              isLoading={isApiRequestsLoading || (startDate && !endDate ? true : false)}
              range={
                moment(endDate)?.diff(moment(startDate), "days")
                  ? moment(endDate)?.diff(moment(startDate), "days") + 1
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <ResponseTimeBarChart
              title={t("responseTime.title")}
              data={
                averageResponseTime?.data.results.every((item) => item.avg_response_time === 0)
                  ? []
                  : (averageResponseTime?.data.results ?? [])
              }
              models={models && models.data?.results ? (models?.data?.results as Model[]) : []}
              graphHeight={requestsAndResponseTimeHeight}
              isLoading={isAverageResponseTimeLoading || (startDate && !endDate ? true : false)}
              range={
                !useMediaQuery(theme.breakpoints.between(1200, 1260)) &&
                moment(endDate)?.diff(moment(startDate), "days")
                  ? moment(endDate)?.diff(moment(startDate), "days") + 1
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <KonanLastFeedbackCard
              date={
                lastFeedbackResponse?.data?.latest_feedback_date_time
                  ? new Date(lastFeedbackResponse?.data?.latest_feedback_date_time)
                  : undefined
              }
              cardHeight={requestsAndResponseTimeHeight}
              isLoading={isLastFeedbackLoading || (startDate && !endDate ? true : false)}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <UsageBarChart
              title={t("cpuUsage.title")}
              data={CPUChartData}
              graphHeight={driftAndUsageHeight}
              isLoading={isCPUAndRAMLoading}
              range={
                moment(endDate)?.diff(moment(startDate), "days")
                  ? moment(endDate)?.diff(moment(startDate), "days") + 1
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <UsageBarChart
              title={t("ramUsage.title")}
              data={RAMChartData}
              graphHeight={driftAndUsageHeight}
              isLoading={isCPUAndRAMLoading}
              isRamUsage
              range={
                moment(endDate)?.diff(moment(startDate), "days")
                  ? moment(endDate)?.diff(moment(startDate), "days") + 1
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <DataDriftBarChart
              title={t("dataDrift.title")}
              data={dataDriftChartData}
              graphHeight={driftAndUsageHeight}
              isLoading={isDataDriftLoading}
            />
          </Grid>
        </Grid>
      </Grid>

      {canViewHistory && (
        <Grid item xs={12} lg={3}>
          <KonanActivityCard
            data={activityData as ({ name: string } & ListModelStateSwitch)[]}
            cardHeight={activityGraphHeight}
            isLoading={isActivityLoading || (startDate && !endDate ? true : false) || isModelsLoading}
          />
        </Grid>
      )}
    </Grid>
  )
}
